import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';
import GuardedCheckoutConsent, { useGuardedCheckout } from './components/GuardedCheckout';
import ProductNav from './components/ProductNav';

const CYBERTECH_PRICE = 'US$14.99';
const DUAL_PRICE = 'US$24.95';

const STYLES = `
  .ct-purchase *, .ct-purchase *::before, .ct-purchase *::after { box-sizing: border-box; }
  .ct-purchase__shell { width: min(1180px, calc(100% - 48px)); margin: 0 auto; }
  .ct-purchase__hero {
    min-height: 560px; position: relative; overflow: hidden; display: flex; align-items: flex-end;
    background: #071018 url('/gaming/cybertech-world.png') center 48% / cover no-repeat;
  }
  .ct-purchase__hero::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, rgba(3,9,14,.96) 0%, rgba(3,9,14,.84) 42%, rgba(3,9,14,.22) 75%),
                linear-gradient(0deg, #071018 0%, transparent 42%);
  }
  .ct-purchase__hero::after {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(circle at 73% 30%, rgba(91,214,244,.18), transparent 34%);
  }
  .ct-purchase__hero-content { position: relative; z-index: 1; width: min(690px, 100%); padding: 72px 0 64px; }
  .ct-purchase__eyebrow { color: #7fe7ff; font-size: 12px; font-weight: 800; letter-spacing: .18em; text-transform: uppercase; }
  .ct-purchase h1 { margin: 12px 0 4px; color: #fff; font-size: clamp(3.4rem, 9vw, 6.8rem); line-height: .9; letter-spacing: -.055em; }
  .ct-purchase__subtitle { margin: 0 0 22px; color: #c5f4ff; font-size: clamp(1rem, 2vw, 1.35rem); font-weight: 700; }
  .ct-purchase__intro { max-width: 610px; margin: 0 0 26px; color: #d3dde5; font-size: 17px; line-height: 1.7; }
  .ct-purchase__price-line { display: flex; align-items: baseline; gap: 8px; margin: 0 0 25px; color: #fff; }
  .ct-purchase__price-line strong { font-size: 32px; letter-spacing: -.04em; }
  .ct-purchase__price-line span { color: #aebbc6; font-size: 15px; }
  .ct-purchase__actions { display: flex; gap: 12px; flex-wrap: wrap; }
  .ct-purchase__button {
    min-height: 48px; border: 1px solid rgba(129,231,255,.55); border-radius: 11px; padding: 0 22px;
    background: linear-gradient(135deg, #0f8ca8, #176078); color: #fff; font: inherit; font-weight: 800;
    letter-spacing: .02em; cursor: pointer; box-shadow: 0 12px 30px rgba(0,139,174,.22);
  }
  .ct-purchase__button:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .ct-purchase__button--secondary { background: rgba(9,22,31,.78); border-color: rgba(255,255,255,.2); box-shadow: none; }
  .ct-purchase__button[disabled] { cursor: not-allowed; filter: saturate(.45); opacity: .58; transform: none; box-shadow: none; }
  .ct-purchase__body { padding: 34px 0 88px; background: radial-gradient(circle at 84% 10%, rgba(36,166,198,.08), transparent 26%), #071018; }
  .ct-purchase__principle {
    margin: 0 0 32px; padding: 22px 26px; border: 1px solid rgba(115,224,249,.2); border-radius: 14px;
    background: rgba(10,28,39,.8); color: #eefbff; text-align: center; font-size: clamp(1rem, 2vw, 1.25rem);
    font-weight: 900; letter-spacing: .06em;
  }
  .ct-purchase__status {
    display: flex; align-items: flex-start; gap: 14px; margin-bottom: 38px; padding: 18px 20px;
    border: 1px solid rgba(255,190,92,.23); border-radius: 12px; background: rgba(91,59,13,.18); color: #e9d8b6;
  }
  .ct-purchase__status-dot { width: 9px; height: 9px; margin-top: 6px; flex: 0 0 auto; border-radius: 50%; background: #ffbd59; box-shadow: 0 0 18px rgba(255,189,89,.6); }
  .ct-purchase__status strong { display: block; margin-bottom: 3px; color: #fff4dc; }
  .ct-purchase__status p { margin: 0; line-height: 1.55; }
  .ct-purchase__heading { margin: 0 0 8px; color: #fff; font-size: clamp(1.8rem, 4vw, 2.6rem); }
  .ct-purchase__lead { max-width: 720px; margin: 0 0 24px; color: #aebbc6; line-height: 1.65; }
  .ct-purchase__plans { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .ct-purchase__plan {
    position: relative; min-width: 0; padding: 28px; border: 1px solid rgba(255,255,255,.11); border-radius: 18px;
    background: linear-gradient(145deg, rgba(17,39,52,.92), rgba(8,20,29,.94)); box-shadow: 0 18px 44px rgba(0,0,0,.24);
  }
  .ct-purchase__plan--dual { border-color: rgba(122,222,245,.31); }
  .ct-purchase__plan-label { margin-bottom: 13px; color: #84e8ff; font-size: 12px; font-weight: 900; letter-spacing: .15em; text-transform: uppercase; }
  .ct-purchase__plan h3 { margin: 0 0 10px; color: #fff; font-size: 24px; }
  .ct-purchase__plan-price { display: flex; align-items: baseline; gap: 7px; margin-bottom: 15px; color: #fff; }
  .ct-purchase__plan-price strong { font-size: 31px; letter-spacing: -.035em; }
  .ct-purchase__plan-price span { color: #9faeba; }
  .ct-purchase__plan p { min-height: 50px; margin: 0 0 20px; color: #b7c4ce; line-height: 1.55; }
  .ct-purchase__plan .ct-purchase__button { width: 100%; }
  .ct-purchase__note { margin: 16px 0 0; color: #81909c; font-size: 12px; line-height: 1.5; }
  @media (max-width: 760px) {
    .ct-purchase__shell { width: min(100% - 30px, 1180px); }
    .ct-purchase__hero { min-height: 610px; background-position: 59% center; }
    .ct-purchase__hero::before { background: linear-gradient(0deg, #071018 0%, rgba(3,9,14,.88) 58%, rgba(3,9,14,.35) 100%); }
    .ct-purchase__hero-content { padding: 170px 0 45px; }
    .ct-purchase__intro { font-size: 15px; }
    .ct-purchase__plans { grid-template-columns: 1fr; }
    .ct-purchase__plan { padding: 23px; }
    .ct-purchase__actions .ct-purchase__button { width: 100%; }
  }
`;

function PurchaseButton({ children, label, available, busy, onClick }) {
  const disabled = !available || busy;
  return (
    <button
      type="button"
      className="ct-purchase__button"
      disabled={disabled}
      aria-label={available ? label : `${label}, checkout unavailable`}
      title={available ? undefined : 'Purchasing is not available yet'}
      onClick={onClick}
    >
      {busy ? 'Opening checkout...' : children}
    </button>
  );
}

export default function CyberTechPurchasePage() {
  const navigate = useNavigate();
  const consentCheckout = useGuardedCheckout();
  const [purchasingAvailable, setPurchasingAvailable] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const [purchasingPlan, setPurchasingPlan] = useState(null);
  const [pageError, setPageError] = useState('');

  useEffect(() => {
    let cancelled = false;
    API.get('/v1/rpg/account')
      .then((response) => {
        if (!cancelled) {
          setPurchasingAvailable(response.data?.purchasing?.available === true);
          setAvailabilityChecked(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPurchasingAvailable(false);
          setAvailabilityChecked(true);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const continueToStripe = (response) => {
    const checkoutUrl = response?.data?.checkout_url;
    if (checkoutUrl) {
      window.location.assign(checkoutUrl);
      return true;
    }
    setPageError('Could not start checkout. Please try again.');
    return false;
  };

  const startCheckout = async (planKey) => {
    setPurchasingPlan(planKey);
    setPageError('');
    try {
      const response = await consentCheckout.start(
        () => API.post('/v1/rpg/checkout', { plan_key: planKey }),
        { purchaseType: 'rpg_worlds', intendedSelector: planKey },
      );
      if (response) continueToStripe(response);
    } finally {
      setPurchasingPlan(null);
    }
  };

  const purchasingBusy = purchasingPlan !== null || consentCheckout.busy;
  const checkoutError = pageError || consentCheckout.error;

  return (
    <main className="ct-purchase">
      <style>{STYLES}</style>
      <ProductNav />

      <section className="ct-purchase__hero" aria-labelledby="cybertech-title">
        <div className="ct-purchase__shell">
          <div className="ct-purchase__hero-content">
            <div className="ct-purchase__eyebrow">Persistent Science-Fiction RPG</div>
            <h1 id="cybertech-title">CYBERTECH</h1>
            <p className="ct-purchase__subtitle">One human. One persistent character. One continuous life.</p>
            <p className="ct-purchase__intro">
              Long-term science-fiction role-playing driven by real game systems with AI narration. Your character, choices and consequences carry forward across sustained play.
            </p>
            <div className="ct-purchase__price-line">
              <strong>{CYBERTECH_PRICE}</strong><span>/ month</span>
            </div>
            <div className="ct-purchase__actions">
              <PurchaseButton
                label="Subscribe to CyberTech"
                available={purchasingAvailable}
                busy={purchasingBusy}
                onClick={() => startCheckout('cybertech')}
              >Subscribe to CyberTech</PurchaseButton>
              <button type="button" className="ct-purchase__button ct-purchase__button--secondary" onClick={() => navigate('/dashboard/rpg/cybertech/more-info')}>
                More Info
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="ct-purchase__body">
        <div className="ct-purchase__shell">
          <p className="ct-purchase__principle">THE GAME DETERMINES WHAT HAPPENED. THE NARRATOR TELLS YOU WHAT HAPPENED.</p>

          {!purchasingAvailable ? (
            <div className="ct-purchase__status" role="status">
              <span className="ct-purchase__status-dot" />
              <div>
                <strong>{availabilityChecked ? 'Purchasing is not available yet' : 'Checking purchasing availability'}</strong>
                <p>{availabilityChecked ? 'CyberTech access will open here when account purchasing is available.' : 'Confirming secure subscription checkout.'}</p>
              </div>
            </div>
          ) : null}

          {checkoutError ? (
            <div className="ct-purchase__status" role="alert">
              <span className="ct-purchase__status-dot" />
              <div><strong>Checkout could not be opened</strong><p>{checkoutError}</p></div>
            </div>
          ) : null}

          <GuardedCheckoutConsent checkout={consentCheckout} onDone={continueToStripe} />

          <h2 className="ct-purchase__heading">Choose your access</h2>
          <p className="ct-purchase__lead">Choose CyberTech on its own, or include both persistent RPG worlds under one combined subscription.</p>

          <div className="ct-purchase__plans">
            <article className="ct-purchase__plan">
              <div className="ct-purchase__plan-label">One world</div>
              <h3>CyberTech</h3>
              <div className="ct-purchase__plan-price"><strong>{CYBERTECH_PRICE}</strong><span>/ month</span></div>
              <p>One persistent science-fiction world for one human player and one continuing character.</p>
              <PurchaseButton
                label="Choose CyberTech"
                available={purchasingAvailable}
                busy={purchasingBusy}
                onClick={() => startCheckout('cybertech')}
              >Choose CyberTech</PurchaseButton>
            </article>

            <article className="ct-purchase__plan ct-purchase__plan--dual">
              <div className="ct-purchase__plan-label">Both worlds</div>
              <h3>Veridian + CyberTech</h3>
              <div className="ct-purchase__plan-price"><strong>{DUAL_PRICE}</strong><span>/ month</span></div>
              <p>Both persistent RPG worlds included under one combined monthly subscription.</p>
              <PurchaseButton
                label="Choose Both Worlds"
                available={purchasingAvailable}
                busy={purchasingBusy}
                onClick={() => startCheckout('dual')}
              >Choose Both Worlds</PurchaseButton>
            </article>
          </div>

          <p className="ct-purchase__note">
            {purchasingAvailable
              ? 'Checkout opens securely in Stripe TEST mode. No live payment is collected.'
              : 'No payment is collected from this page while purchasing is unavailable.'}
          </p>
        </div>
      </section>
    </main>
  );
}
